import { isOpenableUrl } from '@littlebird/ui-utils';
import { useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { TextViewerDialog } from '@/features/inputs-db/text-viewer-dialog';
import { cn } from '@/lib/utils';

import {
  appStroke,
  formatClock,
  formatClockWithSeconds,
  formatClockRange,
  formatDuration,
  hiddenByLabel,
  hiddenByWords,
} from './activity-timeline.format';
import {
  useOpenSnapshotsForCard,
  type ActivitySelection,
  type ActivitySnapshotSelection,
} from './activity-timeline.hooks';
import {
  cardsEndedAt,
  UNKNOWN_APP,
  type ActivityDayShape,
  type FocusCard,
  type Violation,
} from './activity-timeline.model';
import {
  useActivitySnapshotContentQuery,
  type ActivityInterval,
} from './activity-timeline.query';

type DetailActions = {
  onClear: () => void;
  onSelectSnapshot: (snapshotId: number) => void;
  onSelectInterval: (intervalId: string) => void;
};

/**
 * The captured URL, linked only when it is one the browser can safely open.
 *
 * Captures carry whatever the observed window reported, so a `javascript:` string can reach here
 * and must never become a real `href`.
 */
const CapturedUrl = ({ url }: { url: string }) =>
  isOpenableUrl(url) ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="truncate text-xs text-foreground-muted hover:underline"
      title={url}
    >
      {url}
    </a>
  ) : (
    <span className="truncate text-xs text-foreground-muted" title={url}>
      {url}
    </span>
  );

const Field = ({ label, children }: { label: string; children: string }) => (
  <YStack className="min-w-0 gap-0.5">
    <span className="text-xs text-foreground-muted">{label}</span>
    <span className="truncate text-sm" title={children}>
      {children}
    </span>
  </YStack>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <YStack className="gap-2">
    <span className="text-xs text-foreground-muted">{title}</span>
    {children}
  </YStack>
);

const AppDot = ({ hue }: { hue: number | null }) => (
  <span
    className="size-2 shrink-0 rounded-full"
    style={{ background: appStroke(hue) }}
  />
);

const BackRow = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <XStack className="shrink-0 border-b border-border px-3 py-1.5">
    <Button variant="ghost" size="xs" className="gap-1" onClick={onClick}>
      <Icons.chevronLeft className="size-3" />
      {label}
    </Button>
  </XStack>
);

const VIOLATION_ROW_CLASS =
  'rounded-sm border border-warning-6 bg-warning-2 px-2.5 py-1.5 text-left text-xs text-warning-11';

const ViolationList = ({
  violations,
  onSelectSnapshot,
  onSelectInterval,
}: {
  violations: readonly Violation[];
} & Pick<DetailActions, 'onSelectSnapshot' | 'onSelectInterval'>) => (
  <YStack className="gap-1">
    {violations.map((violation, index) => {
      const { target } = violation;
      return (
        <button
          key={`${violation.kind}-${index}`}
          type="button"
          className={cn(VIOLATION_ROW_CLASS, 'hover:bg-warning-3')}
          onClick={() => {
            if (target.kind === 'snapshot') onSelectSnapshot(target.snapshotId);
            else onSelectInterval(target.intervalId);
          }}
        >
          {violation.message}
        </button>
      );
    })}
  </YStack>
);

const DayOverview = ({
  shape,
  appFilter,
  onFilterApp,
  onSelectSnapshot,
  onSelectInterval,
}: {
  shape: ActivityDayShape;
  appFilter: string | null;
  onFilterApp: (app: string | null) => void;
} & Pick<DetailActions, 'onSelectSnapshot' | 'onSelectInterval'>) => {
  const maxFocusedMs = shape.apps[0]?.focusedMs ?? 0;

  return (
    <YStack className="h-full min-w-0 gap-6 overflow-y-auto px-5 py-5">
      <Section title="Focus, by app">
        {shape.apps.length === 0 ? (
          <span className="text-sm text-foreground-muted">
            No focus intervals.
          </span>
        ) : (
          <YStack className="gap-px">
            {shape.apps.map((entry) => {
              const isActive = entry.appKey === appFilter;
              return (
                <button
                  key={entry.appKey}
                  type="button"
                  title={`frontmost ${formatDuration(entry.focusedMs)} · attended ${formatDuration(entry.attendedMs)}\nclick to show only this app on the timeline`}
                  onClick={() => onFilterApp(isActive ? null : entry.appKey)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-sm px-1.5 py-1 text-left hover:bg-surface-hover',
                    isActive && 'bg-surface-active',
                    appFilter !== null && !isActive && 'opacity-50'
                  )}
                >
                  <AppDot hue={entry.hue} />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {entry.appName}
                  </span>
                  <span className="relative h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-border">
                    <span
                      className="absolute inset-y-0 left-0 rounded-full opacity-40"
                      style={{
                        width: `${maxFocusedMs === 0 ? 0 : (entry.focusedMs / maxFocusedMs) * 100}%`,
                        background: appStroke(entry.hue),
                      }}
                    />
                    <span
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{
                        width: `${maxFocusedMs === 0 ? 0 : (entry.attendedMs / maxFocusedMs) * 100}%`,
                        background: appStroke(entry.hue),
                      }}
                    />
                  </span>
                  <span className="w-14 shrink-0 text-right text-xs text-foreground-muted tabular-nums">
                    {formatDuration(entry.focusedMs)}
                  </span>
                </button>
              );
            })}
          </YStack>
        )}
      </Section>

      <Section title="Unobserved">
        {shape.unobservedReasons.length === 0 ? (
          <span className="text-sm text-foreground-muted">
            The observer ran for the whole day so far.
          </span>
        ) : (
          <YStack className="gap-1 px-1.5">
            {shape.unobservedReasons.map((entry) => (
              <XStack key={entry.reason} className="justify-between gap-3">
                <span className="text-sm">{entry.reason}</span>
                <span className="text-xs text-foreground-muted tabular-nums">
                  {formatDuration(entry.durationMs)}
                </span>
              </XStack>
            ))}
          </YStack>
        )}
      </Section>

      <Section
        title={
          shape.violations.length === 0
            ? 'Invariants'
            : `Invariants broken · ${shape.violations.length}`
        }
      >
        {shape.violations.length === 0 ? (
          <span className="text-sm text-foreground-muted">All hold.</span>
        ) : (
          <ViolationList
            violations={shape.violations}
            onSelectSnapshot={onSelectSnapshot}
            onSelectInterval={onSelectInterval}
          />
        )}
      </Section>

      <Section title="Devices">
        <YStack className="gap-1.5 px-1.5">
          {shape.columns.map((column) => (
            <YStack key={column.deviceId} className="min-w-0 gap-0.5">
              <span
                className="truncate font-mono text-xs"
                title={column.deviceId}
              >
                {column.deviceId}
              </span>
              <span className="text-xs text-foreground-muted tabular-nums">
                observed {formatDuration(column.observedMs)} ·{' '}
                {column.cards.length} focus intervals
                {column.cards.some((card) => card.isDerived)
                  ? ' · derived from captures'
                  : ''}
              </span>
            </YStack>
          ))}
        </YStack>
      </Section>
    </YStack>
  );
};

/**
 * How many of a card's captures the list shows before asking to be expanded.
 *
 * A window whose title does not change stays one interval for as long as it is frontmost, so a
 * card can have most of a day's captures, more buttons than the pane can lay out cheaply.
 */
const COLLAPSED_SNAPSHOT_COUNT = 48;

const SnapshotList = ({
  card,
  selectedSnapshotId,
  onSelectSnapshot,
}: {
  card: FocusCard;
  selectedSnapshotId: number | null;
  onSelectSnapshot: (snapshotId: number) => void;
}) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll
    ? card.snapshots
    : card.snapshots.slice(0, COLLAPSED_SNAPSHOT_COUNT);

  return (
    <Section title={`Captures · ${card.snapshots.length}`}>
      <XStack wrap className="gap-1">
        {shown.map((snapshot) => (
          <Button
            key={snapshot.id}
            variant={snapshot.id === selectedSnapshotId ? 'secondary' : 'ghost'}
            size="xs"
            className="tabular-nums"
            title={snapshot.title ?? undefined}
            onClick={() => onSelectSnapshot(snapshot.id)}
          >
            {formatClockWithSeconds(snapshot.capturedAt)}
          </Button>
        ))}
        {shown.length < card.snapshots.length ? (
          <Button variant="ghost" size="xs" onClick={() => setShowAll(true)}>
            {`Show all ${card.snapshots.length}`}
          </Button>
        ) : null}
      </XStack>
    </Section>
  );
};

const SnapshotContent = ({ snapshotId }: { snapshotId: number }) => {
  const content = useActivitySnapshotContentQuery(snapshotId);

  if (content.isPending) {
    return (
      <span className="text-sm text-foreground-muted">Loading contents...</span>
    );
  }
  if (content.error) {
    return (
      <span className="text-sm text-danger-11">
        Could not load contents: {content.error.message}
      </span>
    );
  }
  if (content.data.length === 0) {
    return (
      <span className="text-sm text-foreground-muted">
        This capture has no text.
      </span>
    );
  }

  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
      {content.data}
    </pre>
  );
};

const intervalDurationMs = (interval: ActivityInterval, now: Date) =>
  (interval.end === null ? now.getTime() : new Date(interval.end).getTime()) -
  new Date(interval.start).getTime();

const IntervalDetail = ({
  interval,
  card,
  violations,
  now,
  onClear,
  onSelectSnapshot,
  onSelectInterval,
}: {
  interval: ActivityInterval;
  card: FocusCard | null;
  violations: readonly Violation[];
  now: Date;
} & DetailActions) => {
  const openSnapshots = useOpenSnapshotsForCard();
  const heading =
    card === null
      ? interval.type
      : card.hiddenBy !== null
        ? `${card.appName} · ${hiddenByWords(card.hiddenBy, card.appName)}`
        : (card.label ?? card.appName);

  return (
    <YStack className="h-full min-w-0">
      <BackRow label="Day" onClick={onClear} />
      <YStack className="min-h-0 flex-1 gap-5 overflow-y-auto px-5 py-4">
        <YStack className="gap-1">
          <XStack className="items-center gap-2 text-xs text-foreground-muted">
            {card === null ? null : <AppDot hue={card.hue} />}
            <span className="truncate">
              {card === null ? interval.type : `focus · ${card.appName}`}
              {card?.isDerived ? ' · derived from captures' : ''}
            </span>
          </XStack>
          <h2 className="truncate text-base" title={heading}>
            {heading}
          </h2>
          {interval.url === null ? null : <CapturedUrl url={interval.url} />}
        </YStack>

        {violations.length > 0 ? (
          <ViolationList
            violations={violations}
            onSelectSnapshot={onSelectSnapshot}
            onSelectInterval={onSelectInterval}
          />
        ) : null}

        <Grid className="grid-cols-3 gap-x-4 gap-y-3">
          <Field label="Started">
            {formatClockWithSeconds(interval.start)}
          </Field>
          <Field label="Ended">
            {interval.end === null
              ? 'still open'
              : formatClockWithSeconds(interval.end)}
          </Field>
          <Field label="Duration">
            {formatDuration(intervalDurationMs(interval, now))}
          </Field>
          <Field label="Opened on">{interval.openReason}</Field>
          <Field label="Closed on">{interval.closeReason ?? 'not yet'}</Field>
          {interval.type === 'observation' ? (
            <>
              <Field label="Heartbeat">
                {interval.heartbeatAt === null
                  ? 'none'
                  : formatClockWithSeconds(interval.heartbeatAt)}
              </Field>
              <Field label="Time zone">
                {interval.tzOffsetMinutes === null
                  ? 'unknown'
                  : `UTC${interval.tzOffsetMinutes >= 0 ? '+' : ''}${interval.tzOffsetMinutes / 60}`}
              </Field>
              <Field label="Capabilities">
                {interval.capabilities?.join(', ') ?? 'none'}
              </Field>
            </>
          ) : null}
          {card === null ? null : (
            <>
              <Field label="Present">
                {`${formatDuration(card.presentMs)} of ${formatDuration(card.durationMs)} on this day`}
              </Field>
              <Field label="Bundle id">
                {interval.bundleIdentifier ?? 'none'}
              </Field>
              <Field label="Hidden by">{hiddenByLabel(card.hiddenBy)}</Field>
            </>
          )}
          <Field label="Device">{interval.deviceId}</Field>
          <Field label="Interval id">{interval.id}</Field>
        </Grid>

        {card === null || card.snapshots.length === 0 ? null : (
          <>
            <SnapshotList
              key={card.key}
              card={card}
              selectedSnapshotId={null}
              onSelectSnapshot={onSelectSnapshot}
            />
            <XStack>
              <Button
                variant="ghost"
                size="xs"
                className="gap-1.5"
                onClick={() => openSnapshots(card)}
              >
                Open in ingestion
                <Icons.externalLink className="size-3.5" />
              </Button>
            </XStack>
          </>
        )}
      </YStack>
    </YStack>
  );
};

const GroupDetail = ({
  cards,
  onClear,
  onSelectInterval,
}: {
  cards: FocusCard[];
} & Pick<DetailActions, 'onClear' | 'onSelectInterval'>) => {
  const first = cards[0];
  const endedAt = cardsEndedAt(cards);

  return (
    <YStack className="h-full min-w-0">
      <BackRow label="Day" onClick={onClear} />
      <YStack className="gap-1 border-b border-border px-5 py-4">
        <span className="text-xs text-foreground-muted">
          focus intervals too short to draw one by one at this zoom
        </span>
        <h2 className="text-base">
          {cards.length} intervals ·{' '}
          {formatClockRange(first.startedAt, endedAt)} ·{' '}
          {formatDuration(endedAt.getTime() - first.startedAt.getTime())}
        </h2>
      </YStack>
      <YStack className="min-h-0 flex-1 gap-px overflow-y-auto px-3 py-2">
        {cards.map((card) => (
          <button
            key={card.key}
            type="button"
            onClick={() => onSelectInterval(card.interval.id)}
            className="flex w-full items-center gap-2.5 rounded-sm px-2 py-1 text-left hover:bg-surface-hover"
          >
            <span className="w-16 shrink-0 text-xs text-foreground-muted tabular-nums">
              {formatClockWithSeconds(card.startedAt)}
            </span>
            <AppDot hue={card.hue} />
            <span className="min-w-0 flex-1 truncate text-sm">
              <span className="font-medium">{card.appName}</span>
              {card.label === null ? null : (
                <span className="text-foreground-subtle"> {card.label}</span>
              )}
            </span>
            <span className="w-24 shrink-0 truncate text-right text-[11px] text-foreground-muted">
              {card.interval.openReason}
            </span>
            <span className="w-10 shrink-0 text-right text-xs text-foreground-muted tabular-nums">
              {formatDuration(card.durationMs)}
            </span>
          </button>
        ))}
      </YStack>
    </YStack>
  );
};

const SnapshotDetail = ({
  selection,
  now,
  onClear,
  onSelectSnapshot,
  onSelectInterval,
}: {
  selection: ActivitySnapshotSelection;
  now: Date;
} & DetailActions) => {
  const { card, snapshot } = selection;
  const content = useActivitySnapshotContentQuery(snapshot.id);
  const [isChainOpen, setIsChainOpen] = useState(false);

  return (
    <YStack className="h-full min-w-0">
      {card === null ? (
        <BackRow label="Day" onClick={onClear} />
      ) : (
        <BackRow
          label={`${card.appName} · ${formatDuration(card.durationMs)}`}
          onClick={() => onSelectInterval(card.interval.id)}
        />
      )}
      <YStack className="gap-4 border-b border-border px-5 py-4">
        <YStack className="gap-1">
          <XStack className="items-center gap-2 text-xs text-foreground-muted">
            <AppDot hue={card?.hue ?? null} />
            <span className="truncate">
              capture · {snapshot.app ?? UNKNOWN_APP}
            </span>
          </XStack>
          <h2
            className="truncate text-base"
            title={snapshot.title ?? undefined}
          >
            {snapshot.title ?? snapshot.app ?? UNKNOWN_APP}
          </h2>
          {snapshot.url === null ? null : <CapturedUrl url={snapshot.url} />}
        </YStack>

        <Grid className="grid-cols-3 gap-x-4 gap-y-3">
          <Field label="Captured">
            {formatClockWithSeconds(snapshot.capturedAt)}
          </Field>
          <Field label="Interval">
            {card === null
              ? 'none'
              : `${formatClock(card.interval.start)} · ${formatDuration(intervalDurationMs(card.interval, now))}`}
          </Field>
          <Field label="Kind">{snapshot.kind ?? 'unknown'}</Field>
          <Field label="Input id">{String(snapshot.id)}</Field>
          <Field label="CK job id">{snapshot.ckJobId ?? 'none'}</Field>
          <Field label="Focus interval id">
            {snapshot.focusIntervalId ?? 'none'}
          </Field>
        </Grid>
        {snapshot.uuid === null ? null : (
          <>
            <XStack>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setIsChainOpen(true)}
              >
                Raw input chain
              </Button>
            </XStack>
            <TextViewerDialog
              isOpen={isChainOpen}
              onClose={() => setIsChainOpen(false)}
              inputId={snapshot.uuid}
              itemType="input"
              text={content.data ?? ''}
              textSize={content.data?.length ?? 0}
            />
          </>
        )}

        {card !== null && card.snapshots.length > 1 ? (
          <SnapshotList
            key={card.key}
            card={card}
            selectedSnapshotId={snapshot.id}
            onSelectSnapshot={onSelectSnapshot}
          />
        ) : null}
      </YStack>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <SnapshotContent snapshotId={snapshot.id} />
      </div>
    </YStack>
  );
};

export const ActivityDetail = ({
  shape,
  selection,
  appFilter,
  onFilterApp,
  ...actions
}: {
  shape: ActivityDayShape;
  selection: ActivitySelection | null;
  appFilter: string | null;
  onFilterApp: (app: string | null) => void;
} & DetailActions) => {
  if (selection === null) {
    return (
      <DayOverview
        shape={shape}
        appFilter={appFilter}
        onFilterApp={onFilterApp}
        onSelectSnapshot={actions.onSelectSnapshot}
        onSelectInterval={actions.onSelectInterval}
      />
    );
  }
  if (selection.kind === 'group') {
    return (
      <GroupDetail
        cards={selection.cards}
        onClear={actions.onClear}
        onSelectInterval={actions.onSelectInterval}
      />
    );
  }
  if (selection.kind === 'interval') {
    return (
      <IntervalDetail
        interval={selection.interval}
        card={selection.card}
        violations={shape.violations.filter(
          (violation) => violation.target.intervalId === selection.interval.id
        )}
        now={shape.now}
        {...actions}
      />
    );
  }
  return <SnapshotDetail selection={selection} now={shape.now} {...actions} />;
};
